import { useImperativeHandle, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
    type IVGSCollectForm,
    type VGSCollectStateParams,
    VGSCollectProvider,
    VGSCollectSession,
    useVGSCollectFormInstance,
    useVGSCollectState,
} from "@vgs/collect-js-react";
import { COUNTERPARTY_TIN_GRANT_HEADER } from "@common/constants/counterpartyTin";
import { TinType } from "@common/types/counterparty";
import { CounterpartyTinGrantAction } from "@common/types/counterpartyTin";
import {
    type CounterpartyTinIds,
    invalidateCounterpartyTin,
    mintTinGrant,
} from "@api/counterpartyTin.api";
import { Button } from "@design-system/Button/Button";
import { HStack } from "@design-system/HStack/HStack";
import { InputRoot } from "@design-system/Input/InputParts";
import { Skeleton } from "@design-system/Skeleton/Skeleton";
import { VStack } from "@design-system/VStack/VStack";
import { VGS_TIN_WRITE_PATH, type VgsConfig } from "@lib/vgs/vgsConfig";
import { signInAgain } from "./signInAgain";
import { TIN_TYPE_LABELS } from "./tinType";
import { TinTypePicker } from "./TinTypePicker";
import type {
    TinDraft,
    TinDraftError,
    TinEntryStatus,
    TinSubmitResult,
} from "./useTinDraft";
import { useVgsCollectStatus } from "./useVgsCollectStatus";
import styles from "./CounterpartyTinSection.module.scss";

const TIN_FIELD_NAME = "tin";
const EIN_FORMAT_VALIDATION = "/^\\d{2}-?\\d{7}$/";
const TIN_FIELD_CSS = {
    fontFamily: "inherit",
    fontSize: "14px",
    color: "inherit",
};
interface TinEntryFieldProps {
    config: VgsConfig;
    defaultTinType: TinType;
    draft: TinDraft;
    isSubmitting: boolean;
    onSubmitRequest: () => void;
    onCancel?: () => void;
}

type TinEntrySessionProps = Omit<TinEntryFieldProps, "defaultTinType"> & {
    tinType: TinType;
};

function isSuccessStatus(status: number | string | null): boolean {
    return typeof status === "number" && status >= 200 && status < 300;
}

function getEntryStatus(
    fieldState: VGSCollectStateParams | undefined,
): TinEntryStatus {
    if (!fieldState || fieldState.isEmpty) {
        return "empty";
    }
    return fieldState.isValid ? "valid" : "invalid";
}

function getErrorMessage(error: TinDraftError, tinType: TinType): string {
    switch (error.status) {
        case "invalid":
            return `Enter a valid ${TIN_TYPE_LABELS[tinType]}`;
        case "stepUpRequired":
            return "Sign in again to save this tax ID";
        case "failed":
            return error.message ?? "Failed to save tax ID";
    }
}

async function submitTin({
    form,
    ids,
    tinType,
}: {
    form: IVGSCollectForm | null;
    ids: CounterpartyTinIds;
    tinType: TinType;
}): Promise<TinSubmitResult> {
    if (!form) {
        return { status: "failed", message: null };
    }
    const grantResult = await mintTinGrant({
        ids,
        action: CounterpartyTinGrantAction.WRITE,
    });
    if (grantResult.status !== "granted") {
        return grantResult;
    }
    return await new Promise((resolve) => {
        form.submit(
            VGS_TIN_WRITE_PATH,
            {
                method: "post",
                serialization: "json",
                headers: { [COUNTERPARTY_TIN_GRANT_HEADER]: grantResult.grant },
                data: (fields) => ({ tin: fields[TIN_FIELD_NAME], tinType }),
            },
            (status) =>
                resolve(
                    isSuccessStatus(status)
                        ? { status: "saved" }
                        : { status: "failed", message: null },
                ),
            () => resolve({ status: "failed", message: null }),
        );
    });
}

export const TinEntryField = ({
    defaultTinType,
    ...sessionProps
}: TinEntryFieldProps) => {
    const collectStatus = useVgsCollectStatus(sessionProps.config);
    const [selectedTinType, setSelectedTinType] = useState<TinType | null>(
        null,
    );
    const tinType = selectedTinType ?? defaultTinType;

    if (collectStatus === "loading") {
        return <Skeleton height={32} />;
    }
    if (collectStatus === "failed") {
        return (
            <span className={styles.message}>Failed to load tax ID field</span>
        );
    }

    return (
        <HStack gap={8} align="start">
            <TinTypePicker
                value={tinType}
                disabled={sessionProps.isSubmitting}
                onValueChange={setSelectedTinType}
            />
            <div className={styles.entryBody}>
                <VGSCollectProvider key={tinType}>
                    <TinEntrySession tinType={tinType} {...sessionProps} />
                </VGSCollectProvider>
            </div>
        </HStack>
    );
};

const TinEntrySession = ({
    config,
    tinType,
    draft,
    isSubmitting,
    onSubmitRequest,
    onCancel,
}: TinEntrySessionProps) => {
    const queryClient = useQueryClient();
    const [formInstance] = useVGSCollectFormInstance();
    const form: IVGSCollectForm | null = formInstance || null;
    const [formState] = useVGSCollectState();
    const fieldState = formState?.[TIN_FIELD_NAME];
    const errorMessage = draft.error
        ? getErrorMessage(draft.error, tinType)
        : null;

    useImperativeHandle(
        draft.entryRef,
        () => ({
            status: getEntryStatus(fieldState),
            submit: async (ids) => {
                const result = await submitTin({ form, ids, tinType });
                if (result.status === "saved") {
                    await invalidateCounterpartyTin(queryClient, ids);
                }
                return result;
            },
        }),
        [fieldState, form, tinType, queryClient],
    );

    return (
        <VGSCollectSession
            vaultId={config.vaultId}
            environment={config.environment}
            cname={config.cname ?? undefined}
            onUpdateCallback={(state) => {
                const status = getEntryStatus(state?.[TIN_FIELD_NAME]);
                draft.changeEntry({
                    hasInput: status !== "empty",
                    isValid: status === "valid",
                });
            }}
            onCustomSubmit={(event) => {
                event.preventDefault();
                onSubmitRequest();
            }}
        >
            <VStack gap={4}>
                <HStack gap={8} align="center">
                    <InputRoot
                        size="lg"
                        disabled={isSubmitting}
                        className={styles.tinField}
                        data-focused={fieldState?.isFocused ? "" : undefined}
                        data-invalid={errorMessage ? "" : undefined}
                        ref={(node) => {
                            if (node) {
                                node.inert = isSubmitting;
                            }
                        }}
                    >
                        {tinType === TinType.SSN ? (
                            <VGSCollectSession.SSNField
                                name={TIN_FIELD_NAME}
                                className={styles.tinFrame}
                                placeholder="SSN"
                                validations={["required", "validSSN"]}
                                css={TIN_FIELD_CSS}
                            />
                        ) : (
                            <VGSCollectSession.TextField
                                name={TIN_FIELD_NAME}
                                className={styles.tinFrame}
                                placeholder="EIN"
                                mask="99-9999999"
                                validations={[
                                    "required",
                                    EIN_FORMAT_VALIDATION,
                                ]}
                                css={TIN_FIELD_CSS}
                            />
                        )}
                    </InputRoot>
                    {onCancel && (
                        <Button
                            type="button"
                            variant="secondary"
                            size="lg"
                            disabled={isSubmitting}
                            onClick={onCancel}
                        >
                            Cancel
                        </Button>
                    )}
                </HStack>
                {errorMessage && (
                    <span className={styles.error} role="alert">
                        {errorMessage}
                    </span>
                )}
                {draft.error?.status === "stepUpRequired" && (
                    <HStack>
                        <Button
                            type="button"
                            variant="primary"
                            size="sm"
                            onClick={signInAgain}
                        >
                            Sign in again
                        </Button>
                    </HStack>
                )}
            </VStack>
        </VGSCollectSession>
    );
};
